/**
 * Garante type/location coerentes quando o backend ainda não normalizou (cache / versão antiga).
 * Espelha `leadMessageLocation.js` + `inferLocationMessageType` na API.
 */

function parseParenLatLng(s) {
  if (s == null || typeof s !== "string") return null;
  const m = s.match(/\(\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)/);
  if (!m) return null;
  const latN = Number(m[1]);
  const lngN = Number(m[2]);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;
  if (latN === 0 && lngN === 0) return null;
  return { latitude: latN, longitude: lngN, lat: latN, lng: lngN };
}

function parseGoogleMapsLatLngFromString(s) {
  if (s == null || typeof s !== "string") return null;
  let t = s.trim();
  try {
    t = decodeURIComponent(t);
  } catch {
    /* keep */
  }

  const fromQParam = (param) => {
    const re = new RegExp(`[?&]${param}=([^&]+)`, "i");
    const m = t.match(re);
    if (!m) return null;
    let inner = m[1].replace(/\+/g, " ");
    try {
      inner = decodeURIComponent(inner);
    } catch {
      /* ignore */
    }
    const parts = inner.split(/[,\s]+/).filter((x) => x !== "");
    if (parts.length < 2) return null;
    const latN = Number(parts[0]);
    const lngN = Number(parts[1]);
    if (Number.isFinite(latN) && Number.isFinite(lngN) && !(latN === 0 && lngN === 0)) {
      return { latitude: latN, longitude: lngN, lat: latN, lng: lngN };
    }
    return null;
  }

  let coords = fromQParam("q");
  if (coords) return coords;
  coords = fromQParam("query");
  if (coords) return coords;

  const m = t.match(/\/@(-?\d+\.?\d*),(-?\d+\.?\d*)(?:[,/z]|\?|$)/i);
  if (m) {
    const latN = Number(m[1]);
    const lngN = Number(m[2]);
    if (Number.isFinite(latN) && Number.isFinite(lngN) && !(latN === 0 && lngN === 0)) {
      return { latitude: latN, longitude: lngN, lat: latN, lng: lngN };
    }
  }
  return null;
}

function isLocationTipoEnvio(tipoEnvio) {
  const te = tipoEnvio != null ? String(tipoEnvio) : "";
  if (!te) return false;
  if (te === "LOCALIZACAO_ENVIADA" || te === "LOCALIZACAO_RECEBIDA") return true;
  return /^LOCALIZACAO_/i.test(te);
}

function coordsFromMensagemAndTipo(mensagem, tipoEnvio) {
  if (!isLocationTipoEnvio(tipoEnvio)) return null;
  const raw = mensagem != null ? String(mensagem) : "";
  return parseParenLatLng(raw) || parseGoogleMapsLatLngFromString(raw);
}

function coordsFromWhatsappJsonTree(j) {
  if (!j || typeof j !== "object") return null;

  const tryNode = (node) => {
    if (!node || typeof node !== "object") return null;
    return coordsFromMensagemAndTipo(node.mensagem, node.tipo_envio);
  };

  let c = tryNode(j);
  if (c) return c;

  const st = j.xpro_provider_status;
  if (st && typeof st === "object") {
    for (const v of Object.values(st)) {
      c = tryNode(v);
      if (c) return c;
    }
  }

  const pay = j.xpro_last_status_event?.payload;
  c = tryNode(pay);
  if (c) return c;

  return null;
}

function coordsFromMessageTextIfSoleMapsUrl(messageText) {
  if (messageText == null || typeof messageText !== "string") return null;
  const line = messageText.replace(/\r?\n/g, "").trim();
  if (!line || !/^https?:\/\//i.test(line)) return null;
  if (/\s/.test(line)) return null;
  if (!/google\./i.test(line) && !/maps\./i.test(line)) {
    if (!/\/maps\b/i.test(line) && !/maps\.google/i.test(line)) return null;
  }
  return parseGoogleMapsLatLngFromString(line);
}

function pickPushName(jsonWhatsappRaw) {
  if (!jsonWhatsappRaw) return null;
  try {
    const j = typeof jsonWhatsappRaw === "string" ? JSON.parse(jsonWhatsappRaw) : jsonWhatsappRaw;
    if (j?.pushName != null && String(j.pushName).trim()) return String(j.pushName).trim();
  } catch {
    /* ignore */
  }
  return null;
}

function buildLocationFromMessage(msg) {
  const latDb = msg?.location?.latitude ?? msg?.location?.lat;
  const lngDb = msg?.location?.longitude ?? msg?.location?.lng;
  const latN = latDb != null ? Number(latDb) : NaN;
  const lngN = lngDb != null ? Number(lngDb) : NaN;
  const fromDb =
    Number.isFinite(latN) &&
    Number.isFinite(lngN) &&
    !(latN === 0 && lngN === 0);

  if (fromDb) {
    return {
      latitude: latN,
      longitude: lngN,
      lat: latN,
      lng: lngN,
      ...(msg.location?.name ? { name: msg.location.name } : {}),
      ...(msg.location?.address ? { address: msg.location.address } : {}),
    };
  }

  let j = null;
  if (msg.json_whatsapp) {
    try {
      j = typeof msg.json_whatsapp === "string" ? JSON.parse(msg.json_whatsapp) : msg.json_whatsapp;
    } catch {
      j = null;
    }
  }
  const fromJson = j ? coordsFromWhatsappJsonTree(j) : null;
  const fromMapsLine = coordsFromMessageTextIfSoleMapsUrl(msg.text);
  const picked = fromJson || fromMapsLine;
  if (!picked) return null;

  const pushName = pickPushName(msg.json_whatsapp);
  return {
    ...picked,
    ...(pushName ? { name: pushName } : {}),
  };
}

function isTextOnlyGoogleMapsCoordinateLink(text) {
  const t = text != null && typeof text === "string" ? text.replace(/\r?\n/g, "").trim() : "";
  if (!t || !/^https?:\/\//i.test(t)) return false;
  if (/\s/.test(t)) return false;
  return parseGoogleMapsLatLngFromString(t) != null;
}

function inferType(baseType, text, locationObj) {
  if (!locationObj || typeof locationObj !== "object") return baseType;
  if (baseType === "location") return "location";
  if (baseType !== "text") return baseType;
  const t = text != null && typeof text === "string" ? text.trim() : "";
  if (!t) return "location";
  if (isTextOnlyGoogleMapsCoordinateLink(text)) return "location";
  return baseType;
}

export function enrichMessageLocationFromApi(msg) {
  if (!msg || typeof msg !== "object") return msg;

  const location = buildLocationFromMessage(msg);
  const type = inferType(msg.type, msg.text, location);

  if (type === "location" && location) {
    const prev = msg.location;
    if (
      msg.type === "location" &&
      prev &&
      Number(prev.latitude ?? prev.lat) === Number(location.latitude) &&
      Number(prev.longitude ?? prev.lng) === Number(location.longitude)
    ) {
      return msg;
    }
    return { ...msg, type: "location", location };
  }

  return msg;
}
